import React, { useState, useEffect, useCallback, useRef } from 'react';
import {useNavigate, useSearchParams, useParams, useLocation} from 'react-router-dom';import {useTranslation} from 'react-i18next';import {Users, UserPlus, Search, Check, X, ArrowLeft, Bell, Clock, ChevronRight, Trash2, Loader2, MessageSquare, Share2, Settings, Heart, MessageCircle, ThumbsUp, Rss, Send, Shield} from 'lucide-react';import {Button} from '@/components/ui/button';import {API_URL as API} from '@/utils/apiConfig';import {toast} from 'sonner';import axios from 'axios';
import CommunityFeed from '@/components/CommunityFeed';
import SharedThreadsTab from '@/components/SharedThreadsTab';
import SmartInstallBanner from '@/components/SmartInstallBanner';
import {subscribeToPush, getPushDiagnostics} from '@/services/pushNotifications';
import { requestOneSignalPermission, getOneSignalState } from '@/utils/oneSignalWeb';
import { useSafeBack } from '@/utils/useSafeBack';
// Safe error extraction — prevents crash if detail is object/array
function getErrorMessage(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

// ── Tab Button ──────────────────────────────────────────────────────────────
function Tab({ active, label, count, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap shrink-0 ${
        active
          ? 'text-[var(--theme-text)]'
          : 'text-[var(--theme-textSecondary)] hover:text-[var(--theme-text)]'
      }`}
      style={active ? { background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' } : {}}
    >
      {label}
      {count > 0 && (
        <span
          className="ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-xs font-bold rounded-full"
          style={{ background: 'var(--theme-accent)', color: '#fff' }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ── Friend Card ─────────────────────────────────────────────────────────────
function FriendCard({ friend, onRemove }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const initial = (friend.name || friend.username || '?')[0].toUpperCase();

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl transition-all"
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      data-testid={`friend-card-${friend.user_id}`}
    >
      <button onClick={() => navigate(`/community/user/${friend.user_id}`)} className="shrink-0" data-testid={`friend-avatar-${friend.user_id}`}>
        {friend.picture ? (
          <img src={friend.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
          >
            {initial}
          </div>
        )}
      </button>
      <button onClick={() => navigate(`/community/user/${friend.user_id}`)} className="flex-1 min-w-0 text-left" data-testid={`friend-name-${friend.user_id}`}>
        <p className="text-sm font-medium truncate hover:underline" style={{ color: 'var(--theme-text)' }}>
          {friend.name || 'Unknown'}
        </p>
        {friend.username && (
          <p className="text-xs truncate" style={{ color: 'var(--theme-textSecondary)' }}>
            @{friend.username}
          </p>
        )}
      </button>
      {confirming ? (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="icon"
            className="h-8 w-8 rounded-full text-destructive hover:bg-destructive/10"
            onClick={() => { onRemove(friend.friendship_id); setConfirming(false); }}
            data-testid={`confirm-remove-${friend.user_id}`}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="h-8 w-8 rounded-full hover:bg-[var(--theme-hover)]"
            onClick={() => setConfirming(false)}
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost" size="icon"
          className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive"
          onClick={() => setConfirming(true)}
          data-testid={`remove-friend-${friend.user_id}`}
        >
          <Trash2 className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
        </Button>
      )}
    </div>
  );
}

// ── Request Card ────────────────────────────────────────────────────────────
function RequestCard({ req, type, onAccept, onDecline }) {
  const { t } = useTranslation();
  const u = type === 'incoming' ? req.from_user : req.to_user;
  const initial = (u?.name || u?.username || '?')[0].toUpperCase();

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl transition-all"
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      data-testid={`request-card-${req.request_id}`}
    >
      {u?.picture ? (
        <img src={u.picture} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
      ) : (
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
          style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
        >
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
          {u?.name || 'Unknown'}
        </p>
        {u?.username && (
          <p className="text-xs truncate" style={{ color: 'var(--theme-textSecondary)' }}>
            @{u.username}
          </p>
        )}
      </div>
      {type === 'incoming' ? (
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost" size="icon"
            className="h-8 w-8 rounded-full hover:bg-green-500/10 hover:text-green-500"
            onClick={() => onAccept(req.request_id)}
            data-testid={`accept-request-${req.request_id}`}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDecline(req.request_id)}
            data-testid={`decline-request-${req.request_id}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-textSecondary)' }}>
          {t('community.pending')}
        </span>
      )}
    </div>
  );
}

// ── Search Result Card ──────────────────────────────────────────────────────
function SearchResultCard({ u, onSendRequest, sending }) {
  const { t } = useTranslation();
  const initial = (u.name || u.username || '?')[0].toUpperCase();

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl transition-all"
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      data-testid={`search-result-${u.user_id}`}
    >
      {u.picture ? (
        <img src={u.picture} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
      ) : (
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
          style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
        >
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
          {u.name}
        </p>
        <p className="text-xs truncate" style={{ color: 'var(--theme-textSecondary)' }}>
          {u.username ? `@${u.username}` : t('community.email_user')}
        </p>
      </div>
      <Button
        size="sm"
        className="h-8 rounded-full text-xs px-3"
        style={{ background: 'var(--theme-accent)', color: '#fff' }}
        onClick={() => onSendRequest(u.username ? u.username : null, u.user_id)}
        disabled={sending}
        data-testid={`send-request-${u.user_id}`}
      >
        {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3 mr-1" />}
        {t('community.add')}
      </Button>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function CommunityPage({ user }) {
  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/home');
  const [searchParams, setSearchParams] = useSearchParams();
  const routeParams = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  // Path-based deep-link parser — translates the new push-friendly
  // routes (`/community/requests`, `/community/post/:postId/comment/:commentId`,
  // etc.) into the same tab / highlight / panel state the page already
  // tracks. This mirrors the /reflection-style clean path approach so
  // native push notifications route reliably without `?` query strings
  // (which Despia/iOS treat as external Safari URLs and bounce on).
  // Query-string form is still supported for backward-compatibility.
  const pathSegments = location.pathname.split('/').filter(Boolean); // ['community', 'requests', ...]
  const pathSection = pathSegments[1]; // 'requests' / 'friends' / 'shared' / 'post' / 'notifications'
  const pathInitialTab =
    pathSection === 'requests' ? 'requests' :
    pathSection === 'friends'  ? 'friends'  :
    pathSection === 'shared'   ? 'shared'   :
    pathSection === 'post'     ? 'feed'     :
    null;
  const pathInitialPostId = pathSection === 'post' ? routeParams.postId : null;
  const pathInitialPanelOpen = pathSection === 'notifications';
  // Deep-link to a specific shared item (push notification: /community/shared/:shareItemId)
  const pathInitialShareItemId = pathSection === 'shared' ? routeParams.shareItemId : null;

  const initialTab = pathInitialTab || searchParams.get('tab') || 'feed';
  const [tab, setTab] = useState(initialTab);
  const [highlightPostId, setHighlightPostId] = useState(pathInitialPostId || searchParams.get('post') || null);
  const [highlightCommentId, setHighlightCommentId] = useState(routeParams.commentId || searchParams.get('comment') || null);
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banStatus, setBanStatus] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(pathInitialPanelOpen || searchParams.get('notifications') === 'open');
  // Holds the share_item_id from the deep-link path. Cleared by SharedThreadsTab
  // once it has consumed it (opened the thread + scrolled to the share),
  // so subsequent re-renders don't keep triggering the deep-link logic.
  const [pendingShareItemId, setPendingShareItemId] = useState(pathInitialShareItemId || searchParams.get('item') || null);
  // Local filter for the friends list (no API call — just substring match
  // against name + username). Visible only when the user has 5+ friends.
  const [friendsQuery, setFriendsQuery] = useState('');

  // Add friend state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const searchTimer = useRef(null);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/community/friends`, { withCredentials: true });
      setFriends(res.data.friends || []);
    } catch (err) {
      console.error('Failed to load friends:', err);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/community/friends/requests`, { withCredentials: true });
      setIncoming(res.data.incoming || []);
      setOutgoing(res.data.outgoing || []);
    } catch (err) {
      console.error('Failed to load requests:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const banRes = await axios.get(`${API}/community/ban-status`, { withCredentials: true });
        setBanStatus(banRes.data);
      } catch { /* ignore */ }
      try {
        const countRes = await axios.get(`${API}/community/notifications/unread-count`, { withCredentials: true });
        setUnreadCount(countRes.data.count || 0);
      } catch { /* ignore */ }
      await Promise.all([fetchFriends(), fetchRequests()]);
      setLoading(false);
    };
    load();
  }, [fetchFriends, fetchRequests]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/community/search`, { params: { q }, withCredentials: true });
        setSearchResults(res.data.users || []);
      } catch {
        setSearchResults([]);
      }
      setSearching(false);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchQuery]);

  const handleSendRequest = async (username, userId) => {
    setSending(true);
    try {
      const payload = username ? { username } : { user_id: userId };
      const res = await axios.post(`${API}/community/friends/request`, payload, { withCredentials: true });
      toast.success(res.data.message);
      setSearchQuery('');
      setSearchResults([]);
      await fetchRequests();
      if (res.data.status === 'accepted') {
        await fetchFriends();
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send request'));
    }
    setSending(false);
  };

  const handleAccept = async (requestId) => {
    try {
      await axios.post(`${API}/community/friends/accept/${requestId}`, {}, { withCredentials: true });
      toast.success(t('community.request_accepted'));
      await Promise.all([fetchFriends(), fetchRequests()]);
    } catch (err) {
      toast.error(getErrorMessage(err, t('community.failed_accept')));
    }
  };

  const handleDecline = async (requestId) => {
    try {
      await axios.post(`${API}/community/friends/decline/${requestId}`, {}, { withCredentials: true });
      toast.success(t('community.request_declined'));
      await fetchRequests();
    } catch (err) {
      toast.error(getErrorMessage(err, t('community.failed_decline')));
    }
  };

  const handleRemove = async (friendshipId) => {
    try {
      await axios.delete(`${API}/community/friends/${friendshipId}`, { withCredentials: true });
      toast.success(t('community.friend_removed'));
      await fetchFriends();
    } catch (err) {
      toast.error(getErrorMessage(err, t('community.failed_remove')));
    }
  };

  const totalPending = incoming.length;

  return (
    <div
      className="page-container relative [overflow-x:clip]"
      style={{ background: 'var(--theme-background)' }}
      data-testid="community-page"
    >
      {/* Radial glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, var(--theme-accent) 0%, transparent 70%)' }}
      />
      {/* Main — header is inside page-content so it scrolls with the feed,
           preventing any content from hiding behind it on iOS/Despia */}
      <main className="page-content relative z-10 pb-24">
        {/* Twitter-style sticky sub-header — full-bleed, glass blur,
            back button + title pinned to the LEFT edge, right cluster
            for chat / notifications / settings / guidelines. Matches
            the PageSubHeader pattern used on Study Plans pages. */}
        <header
          className="sticky top-0 z-40 w-full backdrop-blur-md flex flex-col"
          style={{
            background: 'color-mix(in srgb, var(--theme-background) 85%, transparent)',
            borderBottom: '1px solid var(--glass-border, var(--theme-border))',
            paddingLeft: 'max(1rem, var(--sal))',
            paddingRight: 'max(1rem, var(--sar))',
            // Reserve room for status-bar overlay on Despia / iOS PWA /
            // Android display-cutout devices. Zero-cost on web.
            paddingTop: 'var(--safe-area-top, env(safe-area-inset-top, 12px))',
          }}
        >
          <div className="flex items-center w-full py-3">
          <button
            onClick={goBackSafe}
            className="p-2 -ml-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
            data-testid="community-back-btn"
          >
            <ArrowLeft className="h-5 w-5" style={{ color: 'var(--theme-text)' }} />
          </button>
          <h1 className="text-lg font-semibold ml-1" style={{ color: 'var(--theme-text)' }}>
            {t('community.title')}
          </h1>
          <div className="ml-auto flex items-center gap-0.5">
            <button
              onClick={() => navigate('/chat')}
              className="p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
              data-testid="community-chat-btn"
              title={t('generated.communitypage_chat_with_eili_8c89')}
            >
              <MessageCircle className="h-[18px] w-[18px]" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
            <button
              onClick={() => setShowNotifPanel(!showNotifPanel)}
              className="relative p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
              data-testid="community-notifications-bell"
            >
              <Bell className="h-[18px] w-[18px]" style={{ color: 'var(--theme-textSecondary)' }} />
              {unreadCount > 0 && (
                <span
                  className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[14px] h-3.5 px-0.5 text-[9px] font-bold rounded-full"
                  style={{ background: '#ef4444', color: '#fff' }}
                  data-testid="notification-badge"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
              data-testid="community-settings-btn"
              title={t('generated.communitypage_settings_b5e3')}
            >
              <Settings className="h-[18px] w-[18px]" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
            <button
              onClick={() => navigate('/community-guidelines')}
              className="p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
              data-testid="community-guidelines-link"
              title={t('guidelines.title')}
            >
              <Shield className="h-[18px] w-[18px]" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          </div>
          </div>
        </header>

        {/* Feed Content */}
        <div className="px-5 md:px-8 max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto w-full">
        {/* Occasional install nudge — mobile-web only, respects the
            shared cooldown so it doesn't overlap with Reflection page. */}
        <SmartInstallBanner source="community-banner" className="mt-4" />

        {/* Community Ban Banner */}
        {banStatus?.banned && (
          <div
            className="rounded-xl p-4 mb-4 flex items-start gap-3"
            style={{ background: '#fef2f2', border: '1px solid #fecaca' }}
            data-testid="community-ban-banner"
          >
            <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 bg-red-100">
              <X className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-red-800">{t('community.ban_title')}</p>
              <p className="text-xs text-red-600 mt-0.5">
                {banStatus.reason || t('community.ban_default')}
              </p>
              <p className="text-xs text-red-400 mt-1">{t('community.ban_note')}</p>
            </div>
          </div>
        )}

        {/* Search / Add Friend */}
        <div className="mb-6">
          <div
            className="flex items-center gap-2 px-4 py-3 rounded-xl"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          >
            <Search className="h-4 w-4 shrink-0" style={{ color: 'var(--theme-textSecondary)' }} />
            <input
              type="text"
              placeholder={t('community.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none"
              style={{ color: 'var(--theme-text)' }}
              data-testid="community-search-input"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setSearchResults([]); }}>
                <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
              </button>
            )}
          </div>

          {/* Search Results */}
          {searchQuery.trim().length >= 2 && (
            <div className="mt-2 space-y-2" data-testid="search-results">
              {searching ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-accent)' }} />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-center text-sm py-4" style={{ color: 'var(--theme-textSecondary)' }}>
                  {t('community.no_users_found')}
                </p>
              ) : (
                searchResults.map((u) => (
                  <SearchResultCard key={u.user_id} u={u} onSendRequest={handleSendRequest} sending={sending} />
                ))
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1.5 mb-5 overflow-x-auto pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          <Tab active={tab === 'feed'} label={t('community.tab_feed')} onClick={() => setTab('feed')} testId="tab-feed" />
          <Tab active={tab === 'friends'} label={t('community.tab_friends')} count={friends.length} onClick={() => setTab('friends')} testId="tab-friends" />
          <Tab active={tab === 'shared'} label={t('community.tab_shared')} onClick={() => setTab('shared')} testId="tab-shared" />
          <Tab active={tab === 'requests'} label={t('community.tab_requests')} count={totalPending} onClick={() => setTab('requests')} testId="tab-requests" />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
          </div>
        ) : (
          <>
            {/* Feed Tab */}
            {tab === 'feed' && <CommunityFeed userId={user?.user_id} highlightPostId={highlightPostId} onHighlightClear={() => setHighlightPostId(null)} />}

            {/* Friends Tab */}
            {tab === 'friends' && (
              <div className="space-y-2" data-testid="friends-list">
                {friends.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
                    <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>{t('community.no_friends')}</p>
                    <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                      {t('community.no_friends_hint')}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Local search filter — case-insensitive substring
                        match against name and username. Hidden when the
                        user has fewer than 5 friends since scrolling is
                        easier than searching at that size. */}
                    {friends.length >= 5 && (
                      <FriendSearchInput value={friendsQuery} onChange={setFriendsQuery} />
                    )}
                    {(() => {
                      const q = friendsQuery.trim().toLowerCase();
                      const filtered = !q ? friends : friends.filter((f) =>
                        (f.name || '').toLowerCase().includes(q) ||
                        (f.username || '').toLowerCase().includes(q),
                      );
                      if (filtered.length === 0) {
                        return (
                          <p className="text-center py-6 text-sm" style={{ color: 'var(--theme-textSecondary)' }} data-testid="friends-no-match">
                            {t('generated.communitypage_no_friends_match_9d70')}{friendsQuery}"
                                                      </p>
                        );
                      }
                      return filtered.map((f) => <FriendCard key={f.user_id} friend={f} onRemove={handleRemove} />);
                    })()}
                  </>
                )}
              </div>
            )}

            {/* Requests Tab */}
            {tab === 'requests' && (
              <div data-testid="requests-tab">
                {incoming.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs uppercase tracking-wider font-semibold mb-3 px-1" style={{ color: 'var(--theme-textSecondary)' }}>
                      {t('community.incoming')} ({incoming.length})
                    </h3>
                    <div className="space-y-2">
                      {incoming.map((r) => (
                        <RequestCard key={r.request_id} req={r} type="incoming" onAccept={handleAccept} onDecline={handleDecline} />
                      ))}
                    </div>
                  </div>
                )}
                {outgoing.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wider font-semibold mb-3 px-1" style={{ color: 'var(--theme-textSecondary)' }}>
                      {t('community.outgoing')} ({outgoing.length})
                    </h3>
                    <div className="space-y-2">
                      {outgoing.map((r) => (
                        <RequestCard key={r.request_id} req={r} type="outgoing" />
                      ))}
                    </div>
                  </div>
                )}
                {incoming.length === 0 && outgoing.length === 0 && (
                  <div className="text-center py-12">
                    <Clock className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
                    <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>{t('community.no_requests')}</p>
                    <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                      {t('community.no_requests_hint')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Shared With Me Tab */}
            {tab === 'shared' && (
              <SharedThreadsTab
                initialShareItemId={pendingShareItemId}
                onShareConsumed={() => setPendingShareItemId(null)}
              />
            )}
          </>
        )}

        {/* Notification Bell Panel */}
        {showNotifPanel && (
          <div className="fixed inset-0 z-40" data-testid="notification-panel-overlay">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNotifPanel(false)} />
            <div
              className="absolute top-0 right-0 w-full max-w-md h-full overflow-y-auto"
              style={{ background: 'var(--theme-background)', borderLeft: '1px solid var(--glass-border)' }}
              data-testid="notification-panel"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4" style={{ background: 'var(--theme-background)', borderBottom: '1px solid var(--glass-border)', paddingTop: '1rem' }}>
                <h2 className="text-base font-bold" style={{ color: 'var(--theme-text)' }}>{t('community.tab_notifications', 'Notifications')}</h2>
                <button
                  onClick={() => setShowNotifPanel(false)}
                  className="p-2 rounded-full hover:bg-[var(--theme-hover)]"
                  data-testid="close-notification-panel"
                >
                  <X className="h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
                </button>
              </div>
              <div className="p-5">
                <NotificationsTab onNavigate={(targetTab, postId) => {
                  setShowNotifPanel(false);
                  if (postId) setHighlightPostId(postId);
                  setTab(targetTab);
                }} onUnreadChange={(count) => setUnreadCount(count)} />
              </div>
            </div>
          </div>
        )}
        </div>
      </main>
    </div>
  );
}

// ── Notification Settings Modal ──────────────────────────────────────────────

const NOTIF_SETTING_ITEMS = [
  { key: 'friend_requests', labelKey: 'community.notif_friend_requests', descKey: 'community.notif_friend_requests_desc', icon: UserPlus },
  { key: 'friend_accepted', labelKey: 'community.notif_friend_accepted', descKey: 'community.notif_friend_accepted_desc', icon: Check },
  { key: 'comments', labelKey: 'community.notif_comments', descKey: 'community.notif_comments_desc', icon: MessageCircle },
  { key: 'replies', labelKey: 'community.notif_replies', descKey: 'community.notif_replies_desc', icon: MessageSquare },
  { key: 'post_reactions', labelKey: 'community.notif_post_reactions', descKey: 'community.notif_post_reactions_desc', icon: Heart },
  { key: 'comment_reactions', labelKey: 'community.notif_comment_reactions', descKey: 'community.notif_comment_reactions_desc', icon: ThumbsUp },
  { key: 'new_posts', labelKey: 'community.notif_new_posts', descKey: 'community.notif_new_posts_desc', icon: Rss },
  { key: 'shared_content', labelKey: 'community.notif_shared_content', descKey: 'community.notif_shared_content_desc', icon: Share2 },
];

function NotificationSettingsModal({ open, onClose }) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [testingPush, setTestingPush] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [prefsRes, statusRes] = await Promise.all([
          axios.get(`${API}/community/notification-settings`, { withCredentials: true }),
          axios.get(`${API}/notifications/push-status`, { withCredentials: true }),
        ]);
        setPrefs(prefsRes.data);
        setPushStatus(statusRes.data);
        // Run client-side diagnostics
        const diag = await getPushDiagnostics();
        setDiagnostics(diag);
      } catch {
        toast.error(t('community.failed_load_settings'));
      }
      setLoading(false);
    };
    fetchAll();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (key) => {
    if (!prefs) return;
    const newVal = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: newVal }));
    setSaving(true);
    try {
      await axios.put(`${API}/community/notification-settings`, { [key]: newVal }, { withCredentials: true });
      if (key === 'push_enabled' && newVal) {
        // For Despia native apps, OneSignal handles push registration via the native bridge.
        // Only attempt VAPID + OneSignal Web SDK subscription for web/PWA users.
        if (navigator.userAgent.toLowerCase().includes('despia')) {
          toast.success(t('community.push_enabled_toast'));
          setPushStatus({ subscribed: true });
        } else {
          setSubscribing(true);
          // Belt-and-suspenders: ask BOTH channels to subscribe. Either
          // succeeding is enough — the backend dual-delivers on send.
          // 1) OneSignal Web SDK — preferred for browsers + PWA + Android WebView
          //    (sets external_user_id so backend targeting works
          //    immediately on first opt-in, no relink needed).
          // 2) VAPID — fallback for browsers where OneSignal init failed
          //    (e.g. preview env without valid App ID, or SDK CDN block).
          const osPerm = await requestOneSignalPermission();
          const result = await subscribeToPush();
          setSubscribing(false);
          const ok = osPerm === 'granted' || !result.error;
          if (!ok) {
            toast.error(`Push setup: ${result.error || osPerm}`);
          } else {
            toast.success(t('community.push_enabled_toast'));
            setPushStatus({ subscribed: true });
            const diag = await getPushDiagnostics();
            setDiagnostics(diag);
          }
        }
      }
    } catch {
      setPrefs((p) => ({ ...p, [key]: !newVal }));
      toast.error(t('community.failed_save'));
    }
    setSaving(false);
  };

  const handleResubscribe = async () => {
    setSubscribing(true);
    // Try both channels for maximum chance of success.
    const osPerm = await requestOneSignalPermission();
    const result = await subscribeToPush();
    setSubscribing(false);
    const ok = osPerm === 'granted' || !result.error;
    if (!ok) {
      toast.error(`Push failed: ${result.error || osPerm}`);
    } else {
      toast.success(t('community.push_subscribed_toast'));
      setPushStatus({ subscribed: true });
      const diag = await getPushDiagnostics();
      setDiagnostics(diag);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const res = await axios.post(`${API}/notifications/test-push`, {}, { withCredentials: true });
      if (res.data.sent) {
        toast.success(res.data.message || t('community.test_sent'));
      } else {
        // Show specific guidance based on available channels
        const channels = res.data.channels || {};
        if (!channels.vapid && !channels.onesignal) {
          toast.error(t('community.no_push_sub'));
        } else {
          toast.error(res.data.message || t('community.failed_test_push'));
        }
      }
    } catch {
      toast.error(t('community.failed_test_push'));
    }
    setTestingPush(false);
  };

  const handleRegisterNativePush = async () => {
    setSubscribing(true);
    try {
      // Get current user info
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const userId = meRes.data?.user_id;
      if (!userId) {
        toast.error(t('generated.communitypage_could_not_get_f1d2'));
        setSubscribing(false);
        return;
      }

      // Fire the Despia OneSignal bridge call
      const { default: despia } = await import('despia-native');
      despia(`setonesignalplayerid://?user_id=${encodeURIComponent(userId)}`);
      // Retry with delays
      setTimeout(() => despia(`setonesignalplayerid://?user_id=${encodeURIComponent(userId)}`), 2000);
      setTimeout(() => despia(`setonesignalplayerid://?user_id=${encodeURIComponent(userId)}`), 5000);

      // Register with our backend
      await axios.post(`${API}/notifications/register-onesignal`, { player_id: userId }, { withCredentials: true });

      toast.success(t('generated.communitypage_device_registered_for_994d'));
      setPushStatus({ subscribed: true, type: 'onesignal' });
    } catch (err) {
      console.error('Native push registration failed:', err);
      toast.error(t('generated.communitypage_registration_failed_please_1c32'));
    }
    setSubscribing(false);
  };

  if (!open) return null;

  const serverSubscribed = pushStatus?.subscribed;
  const browserHasSub = diagnostics?.hasSubscription;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="notification-settings-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-md overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5"
        style={{
          background: 'var(--theme-surface)',
          border: '1px solid var(--glass-border)',
          maxHeight: 'min(85vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>{t('community.notif_settings_title')}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[var(--theme-hover)]" data-testid="close-notif-settings">
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-accent)' }} />
          </div>
        ) : prefs ? (
          <div className="space-y-1">
            {/* Master push toggle */}
            <div
              className="flex items-center justify-between p-3 rounded-xl mb-3"
              style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: 'var(--theme-accent)' }}>
                  <Bell className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t('community.push_notifications')}</p>
                  <p className="text-[10px]" style={{ color: 'var(--theme-textSecondary)' }}>{t('community.push_desc')}</p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('push_enabled')}
                className="relative w-10 h-5 rounded-full transition-colors"
                style={{ background: prefs.push_enabled ? 'var(--theme-accent)' : 'var(--glass-border)' }}
                data-testid="toggle-push-enabled"
              >
                <div
                  className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform"
                  style={{ left: prefs.push_enabled ? 'calc(100% - 18px)' : '2px' }}
                />
              </button>
            </div>

            <p className="text-[10px] font-medium uppercase tracking-wider px-1 pb-1" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('community.in_app_push')}
            </p>

            {NOTIF_SETTING_ITEMS.map(({ key, labelKey, descKey, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between p-2.5 rounded-xl" style={{ background: 'var(--glass-bg)' }}>
                <div className="flex items-center gap-2.5">
                  <Icon className="h-4 w-4 shrink-0" style={{ color: 'var(--theme-textSecondary)' }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t(labelKey)}</p>
                    <p className="text-[10px]" style={{ color: 'var(--theme-textSecondary)' }}>{t(descKey)}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle(key)}
                  className="relative w-9 h-5 rounded-full transition-colors shrink-0"
                  style={{ background: prefs[key] ? 'var(--theme-accent)' : 'var(--glass-border)' }}
                  data-testid={`toggle-${key}`}
                >
                  <div
                    className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform"
                    style={{ left: prefs[key] ? 'calc(100% - 18px)' : '2px' }}
                  />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-center py-4" style={{ color: 'var(--theme-textSecondary)' }}>{t('community.could_not_load_settings')}</p>
        )}
      </div>
    </div>
  );
}


// ── Notifications Tab (separated for clarity) ───────────────────────────────
function NotificationsTab({ onNavigate, onUnreadChange }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/community/notifications`, { withCredentials: true });
        const notifs = res.data.notifications || [];
        setNotifications(notifs);
        onUnreadChange?.(notifs.filter((n) => !n.read).length);
      } catch {
        toast.error(t('community.failed_load_notifications'));
      }
      setLoading(false);
    };
    fetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/community/notifications/read-all`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      onUnreadChange?.(0);
      toast.success(t('community.all_marked_read'));
    } catch {
      toast.error(t('generated.communitypage_failed_f4eb'));
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/community/notifications/clear-all`, { withCredentials: true });
      setNotifications([]);
      onUnreadChange?.(0);
      toast.success(t('community.notifications_cleared', 'Notifications cleared'));
    } catch {
      toast.error(t('generated.communitypage_failed_to_clear_b126'));
    }
  };

  const handleNotificationClick = async (n) => {
    // Mark as read
    if (!n.read) {
      try {
        await axios.post(`${API}/community/notifications/${n.notification_id}/read`, {}, { withCredentials: true });
        setNotifications((prev) => {
          const updated = prev.map((item) => item.notification_id === n.notification_id ? { ...item, read: true } : item);
          onUnreadChange?.(updated.filter((i) => !i.read).length);
          return updated;
        });
      } catch { /* silent */ }
    }

    // Navigate to relevant content
    const data = n.data || {};
    switch (n.type) {
      case 'friend_request':
        onNavigate?.('requests');
        break;
      case 'friend_accepted':
        onNavigate?.('friends');
        break;
      case 'shared_content':
        onNavigate?.('shared');
        break;
      case 'new_post':
      case 'post_reaction':
      case 'post_comment':
      case 'comment_reply':
      case 'comment_reaction':
        if (data.post_id) {
          onNavigate?.('feed', data.post_id);
        } else {
          onNavigate?.('feed');
        }
        break;
      default:
        onNavigate?.('feed');
    }
  };

  const unread = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
      </div>
    );
  }

  return (
    <div data-testid="notifications-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs font-medium px-3 py-1.5 rounded-full transition-all hover:bg-[var(--theme-hover)]"
              style={{ color: 'var(--theme-accent)' }}
              data-testid="mark-all-read-btn"
            >
              {t('community.mark_all_read')}
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="text-xs font-medium px-3 py-1.5 rounded-full transition-all hover:bg-[var(--theme-hover)]"
              style={{ color: 'var(--theme-textSecondary)' }}
              data-testid="clear-all-notifications-btn"
            >
              {t('community.clear_all', 'Clear All')}
            </button>
          )}
        </div>
        <button
          onClick={() => setShowSettings(true)}
          className="p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
          title={t('generated.communitypage_notification_settings_7c89')}
          data-testid="notification-settings-btn"
        >
          <Settings className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
        </button>
      </div>
      {notifications.length === 0 ? (
        <div className="text-center py-12">
          <Bell className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>{t('community.no_notifications')}</p>
          <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('community.no_notifications_hint')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => handleNotificationClick(n)}
              className="w-full text-left flex items-start gap-3 p-3 rounded-xl transition-all hover:scale-[1.01] active:scale-[0.98]"
              style={{
                background: n.read ? 'var(--glass-bg)' : 'var(--glass-bg-active)',
                border: `1px solid ${n.read ? 'var(--glass-border)' : 'var(--theme-accent)'}`,
                opacity: n.read ? 0.7 : 1,
              }}
              data-testid={`notification-${n.notification_id}`}
            >
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{ background: 'var(--glass-bg-active)' }}
              >
                {n.type === 'friend_request' ? (
                  <UserPlus className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                ) : n.type === 'friend_accepted' ? (
                  <Check className="h-4 w-4" style={{ color: '#22c55e' }} />
                ) : n.type === 'post_reaction' || n.type === 'comment_reaction' ? (
                  <Heart className="h-4 w-4" style={{ color: '#ef4444' }} />
                ) : n.type === 'post_comment' || n.type === 'comment_reply' ? (
                  <MessageCircle className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                ) : n.type === 'shared_content' ? (
                  <Share2 className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                ) : (
                  <Bell className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{n.title}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{n.body}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--theme-textSecondary)', opacity: 0.6 }}>
                  {new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 mt-1">
                {!n.read && (
                  <div className="h-2 w-2 rounded-full" style={{ background: 'var(--theme-accent)' }} />
                )}
                <ChevronRight className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)', opacity: 0.5 }} />
              </div>
            </button>
          ))}
        </div>
      )}
      <NotificationSettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
}

/* ── Reusable filter-input component ──────────────────────────────── */

function FriendSearchInput({ value, onChange, placeholder = 'Search friends...' }) {
  const {
    t
  } = useTranslation();

  return (
    <div className="relative mb-3" data-testid="friends-search-wrap">
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
        style={{ color: 'var(--theme-textSecondary)' }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-9 py-2.5 rounded-full text-sm outline-none transition focus:ring-2"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          color: 'var(--theme-text)',
        }}
        data-testid="friends-search-input"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center hover:bg-[var(--theme-hover)] transition"
          aria-label={t('generated.communitypage_clear_search_5578')}
          data-testid="friends-search-clear"
        >
          <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
        </button>
      )}
    </div>
  );
}
